import { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import PubSub from 'pubsub-js';

import Reload from 'core/components/Icon/Reload';
import Swipe, { Slide } from 'core/components/Swipe';
import Image from 'core/components/Image';
import { SWIPER_MOUNTED } from 'core/site/constants';

import withTheme from 'core/hocs/withTheme';
import withBreakpoint from 'core/hocs/withBreakpoint';

import breakpointPropTypes from 'core/utils/prop-types/breakpoint';
import { isImageModel } from 'core/utils/content';
import themePropTypes from 'core/utils/prop-types/theme';

import styles from './index.styl';

const THUMBS_HEIGHT = 40;
const THUMBS_WIDTH = 40;

class Navigation extends PureComponent {
  state = {
    shouldMoveToLeft: false,
  };

  componentDidMount() {
    this.getSwiperWrapper = PubSub.subscribe(SWIPER_MOUNTED, () => {
      this.wrapperEl = this.swiper.getSlidesWrapperElement();
      if (this.wrapperEl) {
        this.wrapperEl.style.transition = `transform ${this.props.theme.animations.hover}`;
        const childrenTotalWidth = Array.from(this.wrapperEl.children).reduce((totalWidth, current) => {
          const result = totalWidth + current.getBoundingClientRect().width;
          return result;
        }, 0);
        if (childrenTotalWidth >= this.wrapperEl.getBoundingClientRect().width) {
          // eslint-disable-next-line
          this.setState({ shouldMoveToLeft: true });
        }
      }
      this.updateTransform();
    });
  }

  componentDidUpdate() {
    this.updateTransform();
  }

  componentWillUnmount() {
    this.getSwiperWrapper && PubSub.unsubscribe(this.getSwiperWrapper);
  }

  /**
   * Т.к. элементы этого свайпера имеют динамическую ширину
   * и сам свайпер не может корректно обрабатывать такую ситуацию,
   * делаем кастомный расчёт transform'а.
   */
  updateTransform() {
    if (this.wrapperEl) {
      const widthDelta = this.wrapperEl.scrollWidth - this.wrapperEl.clientWidth;
      let translateX = 0;

      if (widthDelta > 0) {
        const {
          images: {
            length: totalItems,
          },
          activeIndex,
        } = this.props;

        translateX = Math.ceil(widthDelta * activeIndex / (totalItems - 1));
      }

      this.wrapperEl.style.transform = `translateX(-${translateX}px)`;
    }
  }

  render() {
    const {
      images,
      activeIndex,
      focusHandler,
      gotoHandler,
      theme,
      disableReloadButton,
      imagesCount,
    } = this.props;

    const {
      controls: {
        gallery: {
          thumb: thumbAtoms,
        },
      },
    } = theme;

    return (
      <Fragment>
        <style jsx>{`
          .${styles.thumb}
            transition ${theme.animations.hover}
            border-radius ${thumbAtoms.borderRadius}px
            &:before
              background ${thumbAtoms.idle.fade}
              transition background ${theme.animations.hover}
            &:after
              border 3px solid ${thumbAtoms.idle.borderColor}
              transition border ${theme.animations.hover}
            &:hover:before
              background ${thumbAtoms.hover.fade}
            &:hover:after
              border-color ${thumbAtoms.hover.borderColor}
          .${styles.activeThumb}
            &:before
            &:hover:before
              background ${thumbAtoms.active.fade}
            &:after
            &:hover:after
              border-color ${thumbAtoms.active.borderColor}
          .${styles.lastThumb}
            & :global([id*="$"])
              fill ${thumbAtoms.reloadIconColor}
        `}</style>
        <Swipe
          onInteraction={focusHandler}
          ref={instance => {this.swiper = instance;}}
          containerClass={styles.container}
          wrapperClass={this.state.shouldMoveToLeft ? styles.wrapperLeft : styles.wrapper}
        >
          {images.map((image, i) => {
            if (!isImageModel(image)) {
              return <Slide key={`ad_thumb_${i}`} />;
            }

            const {
              id: imageId,
              attributes: {
                versions: {
                  original: cover,
                },
              },
            } = image;
            const active = i === activeIndex;

            return (
              <Slide key={imageId}>
                <div
                  className={cx(
                    styles.thumb,
                    active && styles.activeThumb,
                  )}
                  onClick={active ? undefined : () => gotoHandler(i, activeIndex)}
                >
                  <Image
                    src={cover}
                    width={(cover.width / cover.height) * THUMBS_HEIGHT}
                    height={THUMBS_HEIGHT}
                    instant
                  />
                </div>
              </Slide>
            );
          }).concat(
            !disableReloadButton && imagesCount > 10 ? (
              <Slide key={images.length}>
                <div
                  className={cx(styles.thumb, styles.lastThumb)}
                  style={{
                    width: THUMBS_WIDTH,
                    height: THUMBS_HEIGHT,
                  }}
                  onClick={() => gotoHandler(0)}
                >
                  <Reload width='24px' height='24px' />
                </div>
              </Slide>
            ) : null
          )}
        </Swipe>
      </Fragment>
    );
  }
}

const thumbStateAtoms = '{ fade, borderColor }';

Navigation.propTypes = {
  images: PropTypes.array.isRequired,
  activeIndex: PropTypes.number.isRequired,
  focusHandler: PropTypes.func.isRequired,
  gotoHandler: PropTypes.func.isRequired,
  disableReloadButton: PropTypes.bool,
  imagesCount: PropTypes.number,
  breakpoint: breakpointPropTypes(),
  theme: themePropTypes(`
    {
      animations: {
        hover,
      },
      controls: {
        gallery: {
          thumb: {
            reloadIconColor,
            borderRadius,
            idle: ${thumbStateAtoms},
            hover: ${thumbStateAtoms},
            active: ${thumbStateAtoms},
          },
        }
      }
    }
  `),
};

export default withTheme(withBreakpoint(Navigation));
